import React, { useMemo, useCallback, useState } from "react";
import {
  ColumnDef,
  RowSelectionState,
  OnChangeFn,
  Updater,
  Column,
  Row,
} from "@tanstack/react-table";
import { NameAndAvatar } from "../StudentListWrapper/Grid/NameAndAvatar";
import { Checkbox } from "@/components/ui/Checkbox";
import { DataTable } from "@/components/DataTable";
import { Card, Button } from "@/components/ui";
import { useTranslation } from "react-i18next";
import { RiseUserAction } from "@/types/RiseUserAction";
import { Student } from "@/types/Student";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { PaginationVariables } from "@/util/pagination";
import { ExternalLink, CircleX } from "lucide-react";
import { RiseActionGroupMasterID } from "@/types/RiseActionGroupMasterID";
import {
  StudentListRiseActionDetailsSortableFieldEnum,
  SortDirectionEnum,
} from "@/__generated__/types";
import { StudentOrdering, SortableFieldEnum } from "@/types/StudentOrdering";
import { Sort } from "@/components/DataTable/Header/THeaderCellSorting";
import { useRoute } from "@/routes/getRoute";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { EmptyState } from "../DataTable/States/Empty";
import { EmptyStateComponent } from "../EmptyStateComponent";
import { StatusDropdownWrapper } from "./StatusDropdownWrapper";
import { QualifiedChatIdSerializer } from "@/lib/QualifiedChatIdSerializer";
import { useActionStudents } from "./useActionStudents";

const ID_TO_SORT_ENUM: Record<string, SortableFieldEnum> = {
  status: StudentListRiseActionDetailsSortableFieldEnum.Status,
  name: StudentListRiseActionDetailsSortableFieldEnum.Name,
  goingStatus: StudentListRiseActionDetailsSortableFieldEnum.GoingStatus,
  funnelState: StudentListRiseActionDetailsSortableFieldEnum.FunnelState,
};

const PAGE_SIZE = 5;
const CHART_HEIGHT = 372;

// Unfortunately, these must match up with what the css computes to for each column
const CHECKBOX_COL_WIDTH = 56;
const ACTION_COL_WIDTH = 132;

export const ActionStudentsTable = ({
  action,
  selectedStudents,
  onSelectedStudentsChange,
  disabledStudentRowIds,
}: {
  action: RiseUserAction;
  selectedStudents: Student[];
  onSelectedStudentsChange: (rows: Student[]) => void;
  disabledStudentRowIds: string[];
}) => {
  const { t } = useTranslation();
  const getRoute = useRoute();
  const [page, setPage] = useState<number>(1);
  const [ordering, setOrdering] = useState(
    new StudentOrdering(
      StudentListRiseActionDetailsSortableFieldEnum.Name,
      SortDirectionEnum.Asc,
      "rise.actionDetails",
    ),
  );
  const [paginationState, setPaginationState] = useState<PaginationVariables>({
    first: PAGE_SIZE,
  });

  const {
    items,
    startCursor,
    endCursor,
    hasNextPage,
    totalCount,
    hasPreviousPage,
    loading,
    error,
  } = useActionStudents({
    paginationVariables: paginationState,
    ordering,
    actionId: action.id,
    fetchPolicy: "network-only",
  });

  const totalPages = Math.ceil((totalCount || 0) / PAGE_SIZE);

  const onPageChange = (newPage: number) => {
    if (newPage > page) {
      setPaginationState({
        after: endCursor,
        before: undefined,
        first: PAGE_SIZE,
        last: undefined,
      });
    }
    if (newPage < page) {
      setPaginationState({
        after: undefined,
        last: PAGE_SIZE,
        first: undefined,
        before: startCursor,
      });
    }
    setPage(newPage);
  };

  const setRows: OnChangeFn<RowSelectionState> = (
    updaterOrValue: Updater<RowSelectionState>,
  ) => {
    if (typeof updaterOrValue === "function") {
      const newValues = updaterOrValue(selectedRows);
      const students = Object.keys(newValues)
        .map((studentId) => {
          return items.find((item) => item.id === studentId);
        })
        .filter((student) => !!student);
      onSelectedStudentsChange(students);
    }
  };

  const selectedRows = selectedStudents.reduce(
    (acc, cur) => {
      const studentId = cur.id;
      if (studentId) acc[studentId] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const onSort = useCallback(
    (sort: Sort) => {
      const field = ID_TO_SORT_ENUM[sort.orderingField];
      if (field && setOrdering && ordering) {
        setOrdering(
          ordering.clone({ field, direction: sort.orderingDirection }),
        );
      }
    },
    [setOrdering, ordering],
  );

  const renderHeader = useCallback(
    (column: Column<Student>, translationKey: string) => {
      const canSort = !!setOrdering && !!ID_TO_SORT_ENUM[column.id];

      return canSort ? (
        <DataTable.THeaderCellSorting column={column} onSortingChange={onSort}>
          {t(translationKey)}
        </DataTable.THeaderCellSorting>
      ) : (
        <DataTable.THeaderCell>{t(translationKey)}</DataTable.THeaderCell>
      );
    },
    [onSort, t, setOrdering],
  );

  const columns: ColumnDef<Student>[] = useMemo(() => {
    const cols: ColumnDef<Student>[] = [
      {
        id: "select",
        size: CHECKBOX_COL_WIDTH,
        header: ({ table }) => (
          <DataTable.THeaderCell>
            <Checkbox
              checked={
                table.getIsAllRowsSelected() ||
                (table.getIsSomeRowsSelected() && "indeterminate")
              }
              className="border-border-dark"
              disabled={disabledStudentRowIds.length > 0}
              onCheckedChange={(value: boolean) => {
                return table.toggleAllRowsSelected(!!value);
              }}
              aria-label={t("riseActionDetails.table.selectAll")}
            />
          </DataTable.THeaderCell>
        ),
        cell: ({ row }) => {
          return (
            <DataTable.TBodyCell>
              <Checkbox
                className="border-border-dark"
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onCheckedChange={(value: boolean) => row.toggleSelected(value)}
                aria-label={t("riseActionDetails.table.selectRow")}
              />
            </DataTable.TBodyCell>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "name",
        accessorFn: (row) => row.studentOrganizationStatus?.name,
        enableSorting: true,
        meta: {
          label: "name",
        },
        header: ({ column }: { column: Column<Student> }) =>
          renderHeader(column, "riseActionDetails.table.name"),
        cell: ({ row }: { row: Row<Student> }) => {
          return (
            <ProfileLinkWrapper studentId={row.original.id || ""}>
              <DataTable.TBodyCell>
                <NameAndAvatar
                  name={row.original.studentOrganizationStatus?.name}
                  imageUrl={row.original.imageUrl}
                  avatarShape="square"
                  avatarSize="size-12"
                />
              </DataTable.TBodyCell>
            </ProfileLinkWrapper>
          );
        },
      },
      {
        id: "goingStatus",
        accessorKey: "goingStatus",
        enableSorting: true,
        meta: {
          label: "goingStatus",
        },
        header: ({ column }: { column: Column<Student> }) =>
          renderHeader(column, "riseActionDetails.table.goingStatus"),
        cell: ({ row }: { row: Row<Student> }) => {
          const goingStatus =
            row.original.studentOrganizationStatus?.goingStatus;
          return (
            <ProfileLinkWrapper studentId={row.original.id || ""}>
              <DataTable.TBodyCell>
                {goingStatus ? t(`filters.fieldOptions.${goingStatus}`) : ""}
              </DataTable.TBodyCell>
            </ProfileLinkWrapper>
          );
        },
      },
      {
        id: "funnelState",
        accessorKey: "funnelState",
        enableSorting: true,
        meta: {
          label: "goingStatus",
        },
        header: ({ column }: { column: Column<Student> }) =>
          renderHeader(column, "riseActionDetails.table.funnelState"),
        cell: ({ row }: { row: Row<Student> }) => {
          const goingStatus =
            row.original.studentOrganizationStatus?.funnelState;
          return (
            <ProfileLinkWrapper studentId={row.original.id || ""}>
              <DataTable.TBodyCell>
                {goingStatus ? t(`filters.fieldOptions.${goingStatus}`) : ""}
              </DataTable.TBodyCell>
            </ProfileLinkWrapper>
          );
        },
      },
      {
        id: "phoneNumber",
        accessorKey: "phoneNumber",
        enableSorting: false,
        meta: {
          label: "phoneNumber",
        },
        header: () => (
          <DataTable.THeaderCell>
            {t("riseActionDetails.table.phoneNumber")}
          </DataTable.THeaderCell>
        ),
        cell: ({ row }: { row: Row<Student> }) => {
          try {
            const phoneNumber = row.original.studentOrganizationStatus
              ?.phoneNumber
              ? parsePhoneNumberWithError(
                  String(row.original.studentOrganizationStatus?.phoneNumber),
                )
              : null;
            return (
              <ProfileLinkWrapper studentId={row.original.id || ""}>
                <DataTable.TBodyCell>
                  {phoneNumber
                    ? `+${phoneNumber.countryCallingCode} ${phoneNumber.formatNational()}`
                    : ""}
                </DataTable.TBodyCell>
              </ProfileLinkWrapper>
            );
          } catch {
            return <DataTable.TBodyCell>{""}</DataTable.TBodyCell>;
          }
        },
      },
      {
        id: "crmUrl",
        accessorKey: "crmUrl",
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          label: "crmUrl",
        },
        header: () => (
          <DataTable.THeaderCell>
            {t("riseActionDetails.table.crmLink")}
          </DataTable.THeaderCell>
        ),
        cell: ({ row }: { row: Row<Student> }) => {
          const crmUrl = row.original.studentOrganizationStatus?.crmUrl;
          return (
            <DataTable.TBodyCell>
              {crmUrl && (
                <a href={crmUrl || ""}>
                  <ExternalLink className="size-4 text-muted-foreground" />
                </a>
              )}
            </DataTable.TBodyCell>
          );
        },
      },
      {
        id: "status",
        accessorKey: "status",
        enableSorting: true,
        enableColumnFilter: false,
        meta: {
          label: "status",
        },
        header: ({ column }: { column: Column<Student> }) =>
          renderHeader(column, "riseActionDetails.table.status"),
        cell: ({ row }: { row: Row<Student> }) => {
          return (
            <DataTable.TBodyCell>
              <StatusDropdownWrapper
                student={row.original}
                actionId={action.id}
              />
            </DataTable.TBodyCell>
          );
        },
      },
    ];

    if (
      action.actionGroup.masterId === RiseActionGroupMasterID.DIRECT_MESSAGE
    ) {
      cols.push({
        id: "actionButton",
        enableSorting: false,
        size: ACTION_COL_WIDTH,
        header: () => <DataTable.THeaderCell>{""}</DataTable.THeaderCell>,
        cell: ({ row }: { row: Row<Student> }) => {
          return (
            <DataTable.TBodyCell className="w-[116px]">
              <Button variant={"outline"} asChild>
                <NavLink
                  target="_blank"
                  to={getRoute("selectedChat", {
                    chat:
                      QualifiedChatIdSerializer.serialize({
                        chatType: "chat",
                        chatId: row.original.uuid || "",
                      }) || "",
                  })}
                >
                  {t("riseActionDetails.table.dm")}
                </NavLink>
              </Button>
            </DataTable.TBodyCell>
          );
        },
      });
    }

    if (
      action.actionGroup.masterId === RiseActionGroupMasterID.REIVEW_PROFILES
    ) {
      cols.push({
        id: "actionButton",
        enableSorting: false,
        size: ACTION_COL_WIDTH,
        header: () => <DataTable.THeaderCell>{""}</DataTable.THeaderCell>,
        cell: ({ row }: { row: Row<Student> }) => {
          return (
            <DataTable.TBodyCell>
              <Button variant={"outline"}>
                <NavLink
                  target="_blank"
                  to={getRoute("student", {
                    studentId: row.original.id || "",
                  })}
                >
                  {t("riseActionDetails.table.profile")}
                </NavLink>
              </Button>
            </DataTable.TBodyCell>
          );
        },
      });
    }

    return cols;
  }, [t, renderHeader, action, getRoute, disabledStudentRowIds]);

  if (error) {
    return (
      <Card>
        <EmptyStateComponent
          height={`${CHART_HEIGHT}px`}
          message={t("riseActionDetails.table.error")}
          icon={<CircleX className="size-12" />}
        />
      </Card>
    );
  }

  return (
    <div>
      <Card className="overflow-hidden">
        <div className="h-full w-full rounded-md text-sm text-muted-foreground">
          <div className="w-full">
            <DataTable
              columns={columns}
              data={items}
              className={cn("[&_td:first-child]:pr-0")}
              tableHeader={<DataTable.THeader />}
              getRowId={(row: Student) => row.id || ""}
              emptyState={
                <EmptyState
                  className={`h-[${CHART_HEIGHT}px]`}
                  copy={t("riseActionDetails.table.noStudents")}
                />
              }
              tableBody={<DataTable.TBody />}
              paginationState={{ pageIndex: page - 1, pageSize: PAGE_SIZE }}
              isLoading={loading}
              pagination={
                <>
                  <DataTable.PaginationLabel pageCount={totalPages} />
                  <DataTable.PaginationControls
                    onNextPage={
                      hasNextPage &&
                      onPageChange &&
                      (() => onPageChange(page + 1))
                    }
                    onPreviousPage={
                      hasPreviousPage &&
                      onPageChange &&
                      (() => onPageChange(page - 1))
                    }
                  />
                </>
              }
              enableColumnPinning={true}
              enableRowSelection={() => {
                return disabledStudentRowIds.length === 0;
              }}
              rowSelection={selectedRows}
              onRowSelectionChange={setRows}
              initialState={{
                columnPinning: {
                  left: ["select", "name"],
                  right: ["status", "actionButton"],
                },
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

const ProfileLinkWrapper = ({
  children,
  studentId,
}: {
  children: React.ReactNode;
  studentId: string;
}) => {
  const getRoute = useRoute();

  return (
    <>
      <NavLink
        className={"border-red -m-2 flex min-h-[64px] flex-grow items-center"}
        to={getRoute("student", { studentId })}
        target="_blank"
      >
        {children}
      </NavLink>
    </>
  );
};
